// The Trip Name box: a name to type, and a menu of the trips that already
// exist to file this leg or entry under one of them.
//
// It is `PlaceSuggestInput`'s shape -- a plain input plus a styled list, the
// ARIA combobox pattern, options on onMouseDown so the input never blurs before
// a pick lands -- with the two-tier machinery taken out. There is no remote
// tier, so no debounce and no AbortController: the candidates are one item's
// trips (or one logbook's), the caller already reads them in full, and they
// load once on first focus.
//
// What it is FOR is the part worth stating. A trip used to be identified by its
// name, so two unrelated day cruises both called "Day" were one trip -- one
// page, one share link, one row in the list. A trip now has an id and the name
// is a label (TripIdentity.mjs), and this box is where that id is written:
//
//   * picking a trip BINDS it -- this record joins that exact trip, however it
//     is spelled and however many other trips share the spelling;
//   * "Start a new trip" MINTS one -- the deliberate way to make a second
//     "Day", and the reason picking is offered even when the name already
//     matches something;
//   * typing a diverging name UNBINDS -- the same gesture as a waypoint's
//     "typing a new name unplaces a placed point" (TripRoute.nameOwnsPosition),
//     and the same behaviour typing here has always had: the record leaves the
//     trip. Divergence is measured on the normalized name, so fixing whitespace
//     or capitalisation is not a divergence, and typing the focus-time name
//     back in the same session restores the binding rather than stranding it.
//
// Typing a name and picking nothing is deliberately NOT a mint: it leaves the
// record keyed by name, exactly as before ids existed. That is what keeps a
// stray keystroke on a legacy leg from splitting it away from the siblings it
// still shares a name with -- the menu is how the new semantics are asked for,
// and it offers both answers every time.

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { normalizeTripName, newTripGroupId } from '../fleet-shared/TripIdentity.mjs';
import { tripGroupDateRange } from '../fleet-shared/TripGroups.mjs';

// The list is one item's trips, not a whole gazetteer, but a boat with ten
// seasons behind it still shouldn't drop a hundred rows over the form.
const MAX_TRIP_SUGGESTIONS = 8;

// The rows to show for `text`: every trip whose name contains it, best-effort
// ranked with prefix matches first so typing "Des" puts "Desolation 2026" above
// "Cape Deception". A blank box shows the most recent trips rather than
// nothing -- the candidates are already in memory, and "which trips do I have"
// is the question someone opening this box most often has.
export function filterTripSuggestions(trips, text) {
  const query = normalizeTripName(text);
  const rows = (trips || []).filter((trip) => trip && trip.name);
  if (query === '') return rows.slice(0, MAX_TRIP_SUGGESTIONS);
  const matches = rows.filter((trip) => normalizeTripName(trip.name).includes(query));
  const starts = matches.filter((trip) => normalizeTripName(trip.name).startsWith(query));
  const rest = matches.filter((trip) => !normalizeTripName(trip.name).startsWith(query));
  return [...starts, ...rest].slice(0, MAX_TRIP_SUGGESTIONS);
}

const TripSuggestInput = ({
  id,
  size,
  value,
  tripGroupId,
  placeholder,
  ariaLabel,
  loadSuggestions,
  // Trip Name is the FIRST field on the trip detail page, so unlike the place
  // box this one has to be able to take initial focus -- that is what makes a
  // freshly created leg ready to be named without a click.
  autoFocus,
  inputRef,
  onChange,
  onPick,
  onFocus,
  onBlur,
}) => {
  const listboxId = useId();
  const [trips, setTrips] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Controlled by `value` alone, like PlaceSuggestInput -- no local mirror of
  // the text. `onChange` goes through useAutosave's setFormState, which updates
  // the form synchronously (only the SAVE is debounced), so `value` already
  // reflects the last keystroke and a second copy could only drift from it.
  const shown = value ?? '';

  // The binding as of the last deliberate commitment -- focus, or a pick. What
  // "typing the name back restores the trip" compares against, and why a pick
  // updates it: after picking "Desolation 2026", typing a character and
  // deleting it must restore THAT trip, not whatever the box held on focus.
  const bindingRef = useRef({ name: value ?? '', tripGroupId: tripGroupId ?? null });

  // Whether the box still has focus, and whether the component is still
  // mounted: the candidate load is a round trip and can land after either has
  // stopped being true, and popping a menu open over a settled row -- or
  // setting state on an unmounted input -- is the failure that guards against.
  const focusedRef = useRef(false);
  const mountedRef = useRef(true);

  // The load runs at most once per mount; a second focus reuses the answer.
  const requestedRef = useRef(false);

  // Trip Name is the first field on the trip page, so it takes focus the moment
  // a leg is opened -- and that focus is the page's doing, not the user's.
  // Treating it as an intent to browse trips would make opening any leg pay for
  // a full read of the item's legs it otherwise never makes, and would drop a
  // menu over the form nobody asked for. So the FIRST focus, when this field is
  // the one being auto-focused, is sat out; typing (handleChange) or focusing it
  // deliberately later both load.
  const pendingAutoFocusRef = useRef(Boolean(autoFocus));

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const suggestions = useMemo(() => filterTripSuggestions(trips, shown), [trips, shown]);

  // The rendered rows: the matching trips, then "Start a new trip named X".
  //
  // That last row is offered even when the name matches an existing trip
  // exactly -- that case is precisely the second "Day", and refusing it there
  // would refuse the feature. It is built inside the memo rather than beside
  // it because a fresh object every render would defeat the memo entirely.
  const options = useMemo(() => {
    const typed = shown.trim();
    return [
      ...suggestions.map((trip) => ({ kind: 'trip', trip })),
      ...(typed === '' ? [] : [{ kind: 'new', trip: { name: typed } }]),
    ];
  }, [suggestions, shown]);

  const expanded = open && options.length > 0;

  function close() {
    setOpen(false);
    setActiveIndex(-1);
  }

  function ensureLoaded() {
    if (requestedRef.current || !loadSuggestions) return;
    requestedRef.current = true;
    // A menu that can't be built is a plain text box, which is exactly what
    // this field was before -- never a reason to block or report. Same bargain
    // the rename dialog makes when it can't compute its collision warning.
    Promise.resolve()
      .then(() => loadSuggestions())
      .then((loaded) => {
        if (!mountedRef.current) return;
        setTrips(Array.isArray(loaded) ? loaded : []);
      })
      .catch(() => {
        if (mountedRef.current) setTrips([]);
      });
  }

  function emit(nextText) {
    // Same normalized comparison the grouping itself uses, so "day " and "Day"
    // are not a divergence and cannot silently take a record out of its trip.
    const same = normalizeTripName(nextText) === normalizeTripName(bindingRef.current.name);
    onChange({
      name: nextText,
      tripGroupId: same ? bindingRef.current.tripGroupId : null,
    });
  }

  function handleChange(nextText) {
    // Typing IS the intent an auto-focus wasn't, so it loads whether or not a
    // deliberate focus ever happened -- otherwise naming a freshly created leg
    // would never see a suggestion.
    pendingAutoFocusRef.current = false;
    ensureLoaded();
    setOpen(true);
    // A new query is a genuinely new list, and unlike the place box nothing
    // ever appends to it later, so the index resets here and nowhere else.
    setActiveIndex(-1);
    emit(nextText);
  }

  function pick(option) {
    // "Start a new trip" mints; picking an existing one takes its id. A minted
    // id is safe here in a way an automatic one would not be: the user chose
    // the row that says a new trip is what they want.
    const picked = {
      name: option.trip.name,
      tripGroupId: option.kind === 'new' ? newTripGroupId() : option.trip.tripGroupId,
    };
    bindingRef.current = picked;
    close();
    onPick(picked);
  }

  function handleKeyDown(event) {
    if (!expanded) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? options.length - 1 : index - 1));
    } else if (event.key === 'Enter') {
      // Only an arrowed-to row hijacks Enter; otherwise the key stays native so
      // the box behaves like the plain input it replaced.
      if (activeIndex >= 0) {
        event.preventDefault();
        pick(options[activeIndex]);
      }
    } else if (event.key === 'Escape') {
      // While the list is open, Escape means "close the list", not whatever an
      // enclosing modal would do with it.
      event.stopPropagation();
      close();
    } else if (event.key === 'Tab') {
      close();
    }
  }

  return (
    <div className="position-relative">
      <Form.Control
        id={id}
        ref={inputRef}
        size={size}
        type="text"
        role="combobox"
        autoFocus={autoFocus}
        value={shown}
        aria-label={ariaLabel}
        aria-expanded={expanded}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        placeholder={placeholder}
        onChange={(event) => handleChange(event.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          focusedRef.current = true;
          bindingRef.current = { name: value ?? '', tripGroupId: tripGroupId ?? null };
          const auto = pendingAutoFocusRef.current;
          pendingAutoFocusRef.current = false;
          if (auto) return;
          ensureLoaded();
          setOpen(true);
          if (onFocus) onFocus();
        }}
        onBlur={(event) => {
          focusedRef.current = false;
          close();
          if (onBlur) onBlur(event);
        }}
      />
      {expanded && (
        <ul
          className="dropdown-menu show w-100"
          style={{ top: '100%', left: 0 }}
          role="listbox"
          id={listboxId}
          data-testid="trip-suggestions"
        >
          {options.map((option, index) => (
            <li
              key={option.kind === 'new' ? 'new-trip' : option.trip.key}
              id={`${listboxId}-option-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={`dropdown-item small${index === activeIndex ? ' active' : ''}`}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                pick(option);
              }}
            >
              {option.kind === 'new' ? (
                <>Start a new trip named &ldquo;{option.trip.name}&rdquo;</>
              ) : (
                <>
                  {option.trip.name}{' '}
                  {/* The dates are the whole point of the row: they are how a
                      user tells two trips called "Day" apart. */}
                  <span className={index === activeIndex ? undefined : 'text-body-secondary'}>
                    {tripGroupDateRange(option.trip)}
                    {option.trip.tripGroupId === tripGroupId ? ' · current' : ''}
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TripSuggestInput;
